import { getDistance } from '../utils/math.js';
import { ANIMATION_CONFIG } from '../config.js';

// Base class for all resources
export class Resource {
  constructor(x, y, amount = 1) {
    this.x = x;
    this.y = y;
    this.startX = x;
    this.startY = y;
    this.amount = amount;
    this.active = true;
    this.pulseTimer = 0;
    
    // Common animation properties
    this.isBeingCollected = false;
    this.collectorTarget = null;
    this.pickupProgress = 0;
    this.pickupSpeed = ANIMATION_CONFIG.RESOURCE_PICKUP_SPEED;
    this.scale = 0.0; // Start at 0 for spawn animation
    
    // Spawn animation
    this.spawnProgress = 0;
    this.spawnSpeed = ANIMATION_CONFIG.RESOURCE_SPAWN_SPEED;
    this.isSpawning = true;
  }
  
  // Get resource type (for backward compatibility and debugging)
  get type() {
    return this.constructor.name.toLowerCase();
  }
  
  // Common update logic
  update(delta) {
    // Update pulse animation
    this.pulseTimer += ANIMATION_CONFIG.RESOURCE_PULSE_SPEED;
    
    // Handle spawn animation
    if (this.isSpawning) {
      this.spawnProgress += this.spawnSpeed;
      if (this.spawnProgress >= 1.0) {
        this.spawnProgress = 1.0;
        this.isSpawning = false;
        this.scale = 1.0;
      } else {
        // Ease out cubic for nice "pop in" effect
        const t = this.spawnProgress;
        this.scale = t * t * t;
      }
    }
    
    // Handle pickup animation
    else if (this.isBeingCollected && this.collectorTarget) {
      this.pickupProgress += this.pickupSpeed;
      // console.log(`Pickup progress: ${this.pickupProgress}`);
      if (this.pickupProgress >= 1.0) {
        // Don't set active = false here - let the gathering state do it after calling collect()
        this.pickupProgress = 1.0;
        // console.log(`Pickup progress complete for ${this.type}`);
      } else {
        // Animate movement toward collector
        const t = this.pickupProgress;
        const eased = t * t * (3 - 2 * t); // Smooth step
        
        this.x = this.startX + (this.collectorTarget.x - this.startX) * eased;
        this.y = this.startY + (this.collectorTarget.y - this.startY) * eased;
        
        // Scale down as it approaches
        this.scale = 1.0 - (eased * (1.0 - ANIMATION_CONFIG.RESOURCE_FINAL_SCALE));
      }
    }
    
    // Call subclass-specific update
    this.updateSpecific(delta);
  }
  
  // Subclasses should override this
  updateSpecific(delta) {
    // Override in subclasses for type-specific updates
  }
  
  // Check if can be collected by a unit
  canBeCollectedBy(soldier) {
    // Override in subclasses for specific collection rules
    return false;
  }
  
  // Start collection process
  startCollection(collector) {
    // Default behavior for pickup animation
    // console.log("startCollection of resource", this.type, "by", collector);
    this.isBeingCollected = true;
    this.collectorTarget = collector;
    this.startX = this.x;
    this.startY = this.y;
    this.pickupProgress = 0;
  }
  
  // Stop collection (mainly for nebula)
  stopCollection(collector) {
    // Override in subclasses if needed
  }
  
  // Collect the resource
  collect(collector) {
    // Override in subclasses
    return { type: this.type, amount: this.amount };
  }
  
  // Check if collection animation is complete
  isFullyCollected() {
    let isComplete = this.isBeingCollected && this.pickupProgress >= 1.0;
    // console.log("isFullyCollected", isComplete);
    return isComplete;
  }
  
  // Render the resource
  render(ctx) {
    if (!this.active) return;
    
    // Calculate common visual properties
    const pulse = Math.sin(this.pulseTimer) * 0.3 + 0.7;
    const alpha = this.isBeingCollected ? pulse * this.scale : pulse;
    
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(this.x, this.y);
    
    // Call subclass-specific rendering
    this.renderSpecific(ctx, pulse);
    
    ctx.restore();
  }
  
  // Subclasses must implement this
  renderSpecific(ctx, pulse) {
    throw new Error('renderSpecific must be implemented by subclass');
  }
}